import React, { useMemo, useRef } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/button';
import { Loader2, Plus, Users, LucideIcon } from 'lucide-react';
import CustomerCard from '@/domains/customers/components/CustomerCard';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { useInfiniteAccountCustomers, useDeleteCustomer } from '@/domains/customers/services/account-customers';
import { Customer } from '@/shared/types/customerTypes';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import EmptyView from '@/shared/components/EmptyView';
import SearchBar from '@/shared/components/SearchBar';
import { Organization, User } from '@/shared/types/userTypes';
import ManageCustomerDialog from '@/domains/customers/dialogs/ManageCustomerDialog';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const refetchCustomerData = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['customers-infinite'] });
};

interface CustomersTabProps {
  organization: Organization;
  currentUser: User;
  onViewCustomer: (customer: Customer) => void;
  onAddCustomer: () => void;
}

export const CustomersTab = ({ organization, currentUser, onViewCustomer, onAddCustomer }: CustomersTabProps) => {
  const { hasPermission } = usePermissions();

  // Dialog states
  const [isManageCustomerDialogOpen, setIsManageCustomerDialogOpen] = React.useState(false);
  const [isRemoveOrDeleteCustomerDialogOpen, setIsRemoveOrDeleteCustomerDialogOpen] = React.useState(false);
  const [customer, setCustomer] = React.useState<Customer | null>(null);
  const pendingRemoveOrDelete = useRef<{ customerId: string, isDelete: boolean } | null>(null);
  const removeOrDeleteCustomer = useDeleteCustomer();
  const queryClient = useQueryClient();
  const {
    data: customersInfiniteData,
    isLoading: isLoadingCustomers,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteAccountCustomers();

  const customers = useMemo(() => {
    const pages = customersInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: Customer[] = [];
    for (const page of pages) {
      for (const c of page.results ?? []) {
        if (!seen.has(c.customerId)) {
          seen.add(c.customerId);
          merged.push(c);
        }
      }
    }
    return merged;
  }, [customersInfiniteData?.pages]);

  const totalCount = customersInfiniteData?.pages[0]?.totalCount ?? 0;
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;
    const searchLower = searchTerm.toLowerCase();
    return customers.filter(customer => customer.name?.toLowerCase().includes(searchLower));
  }, [customers, searchTerm]);

  const onManageCustomer = (customer: Customer) => {
    setIsManageCustomerDialogOpen(true);
    setCustomer(customer);
  };

  // Customer Actions
  const onRemoveOrDeleteCustomer = (customerId: string, isDelete: boolean) => {
    setIsRemoveOrDeleteCustomerDialogOpen(true);
    pendingRemoveOrDelete.current = { customerId, isDelete };
  };

  const handleConfirmDeleteCustomer = async () => {
    try {
      await removeOrDeleteCustomer.mutateAsync({
        customerId: pendingRemoveOrDelete.current.customerId
      });
      showSuccess(SUCCESS.CUSTOMER.DELETED);
      refetchCustomerData(queryClient);
    } catch (error) {
      showErrorWithApiError(error, ERROR.CUSTOMER.DELETE);
    } finally {
      setIsRemoveOrDeleteCustomerDialogOpen(false);
      pendingRemoveOrDelete.current = null;
    }
  };

  const renderResourceGrid = (items: Customer[], renderItem: (item: Customer) => React.ReactNode, EmptyIcon: LucideIcon, emptyTitle: string, emptyDescription: string) => {
    if (items.length === 0) {
      return (
        <EmptyView
          icon={EmptyIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      );
    }

    return (
      <div className="flex-1 overflow-y-auto space-y-4">
        <div className="space-y-4">
          {items.map(renderItem)}
        </div>

        <div className="glass-panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
          <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-md">
              Loaded <span className="font-medium">{customers.length}</span>
              {' '}of <span className="font-bold">{totalCount}</span> customers
            </span>
            {filteredCustomers.length !== customers.length && (
              <span className="text-sm">
                ({filteredCustomers.length} match current search query)
              </span>
            )}
          </div>
          {hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-2 self-start sm:self-auto"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </>
              ) : (
                'Load more'
              )}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {/* Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Customers</h1>
            <p className="text-lg">Manage account customers</p>
          </div>
          {onAddCustomer && hasPermission(OrgType.TENANT, Feature.CUSTOMERS, Permission.MANAGE) && (
            <Button
              variant="default"
              onClick={onAddCustomer}
              className="gap-2"
              disabled={isLoadingCustomers}>
              <Plus className="h-4 w-4" />
              Add Customer
            </Button>
          )}
        </div>

        {customers && customers.length > 0 && (
          <>
            {/* Search Section */}
            <div className="relative">
              <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search customers by name..." />
            </div>
          </>
        )}

        {/* Content */}
        {isLoadingCustomers ? (
          <div className="flex justify-center items-center h-32 gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading customers...</p>
          </div>
        ) : (
          renderResourceGrid(
            filteredCustomers || [],
            (customer) => (
              <CustomerCard
                key={customer.customerId}
                customer={customer}
                onRemoveOrDeleteCustomer={(isDelete: boolean) => onRemoveOrDeleteCustomer(customer.customerId, isDelete)}
                onViewCustomer={() => onViewCustomer(customer)}
                onManageCustomer={() => onManageCustomer(customer)}
              />
            ),
            Users,
            searchTerm ? "No Matching Customers" : "No Customers",
            searchTerm ? "Try adjusting your search criteria." : "Add your first customer to get started."
          )
        )}
      </div>

      {/* Delete Customer Dialog */}
      <GenericAlertDialog
        open={isRemoveOrDeleteCustomerDialogOpen}
        onOpenChange={setIsRemoveOrDeleteCustomerDialogOpen}
        title={pendingRemoveOrDelete.current?.isDelete ? "Delete Customer" : "Remove Customer"}
        description={pendingRemoveOrDelete.current?.isDelete ? "Are you sure you want to delete this customer?" : "Are you sure you want to remove this customer from this account?"}
        confirmLabel={pendingRemoveOrDelete.current?.isDelete ? "Delete Customer" : "Remove Customer"}
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteCustomer}
        loading={removeOrDeleteCustomer.isPending}
      />

      {/* Manage Customer Dialog */}
      {customer && (
        <ManageCustomerDialog
          open={isManageCustomerDialogOpen}
          organization={organization}
          customer={customer}
          currentUserId={currentUser?.userId}
          onOpenChange={setIsManageCustomerDialogOpen}
          onSuccess={() => {
            refetchCustomerData(queryClient);
            setCustomer(null);
          }}
        />
      )}
    </>
  );
}
